import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { AppearanceContextType, useAppearanceContext } from "@contexts/AppearanceContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { doesCurrentVersionMeetRequirement, showMainWindow } from "@desktop/ToDesktopService";
import { ToDesktopUtil } from "@desktop/index";
import { FontSize } from "@themes/Fonts";
import Styles from "@themes/Styles";
import { getEnvironmentForApp } from "@util/AppUtil";
import { MenubarFilterService } from "../../../features/task-management/MenubarFilterService";
import useTaskData from "../../../features/task-management/useTaskData";
import CalendarSidebar from "../../calendar/components/CalendarSidebar";
import useProjectsData from "../../projects/useProjectsData";
import { widgetData } from "../../widget-workspace/WidgetDataManager";
import TaskListWidget from "../../widget-workspace/components/Widget/TaskListWidget/TaskListWidget";

const FOOTER_HEIGHT_PX = 36;
const CONTEXT_MENU_DEFAULT_WIDTH_PX = 0;

const AppContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;

    // NOTE: Height and Width applied inline

    // Allows context menu to slide in and out
    transition: width 0.5s ease;

    background-color: ${(props) => props.theme.backgroundSpecial};
    border-radius: 7px;
`;

const AppWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;

    height: 100%;
    width: 100%;
`;

const AppHeader = styled.div`
    width: 100%;
`;

const AppBody = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    padding-top: 10px;

    overflow-y: hidden;
    width: 100%;
`;

const AppComponents = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 0 10px;
    width: 100%;
    height: 100%;
`;

const BodyHalf = styled.div`
    height: 100%;
    width: 49%;
    overflow-y: hidden;
    border-radius: 7px;
`;

const StyledCalendar = styled(CalendarSidebar)`
    width: 100%;
    border-radius: 7px;

    .rbc-calendar[style] {
        width: inherit !important;
    }

    .time-slot-text-label {
        font-size: ${FontSize.mini};
    }

    .toolbar-today-button {
        padding: 4px 1px;
    }
`;

const StyledTaskList = styled(TaskListWidget)`
    height: 100% !important;
    width: 100% !important;
    position: relative;
    z-index: ${Styles.zIndexWidgetContents};
    min-height: 100% !important;
    max-width: unset !important;
    max-height: unset !important;
    border-radius: 7px;

    .widget-header {
        cursor: unset;
    }
`;

const AppFooter = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 5px 15px 5px 5px;

    height: ${FOOTER_HEIGHT_PX}px;
    width: 100%;

    background-color: inherit;
`;

const FooterUnit = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
    width: 15%;
`;

const FooterButton = styled.span`
    cursor: pointer;
    color: ${(props) => props.theme.textColorPrimary};
    font-size: ${FontSize.small};
`;

type MenubarViewOption = "small" | "medium" | "large";

const VERSIONS_WITH_TALL_MENUBAR = {
    LOCAL: "1.7.0",
    TEST: "1.9.0",
    PROD: "1.6.0",
};

const getHeightForAppVersion = () => {
    const env = getEnvironmentForApp();
    let meetsVersionReq = false;
    switch (env) {
        case "local":
            meetsVersionReq = doesCurrentVersionMeetRequirement(VERSIONS_WITH_TALL_MENUBAR.LOCAL);
            break;
        case "test":
            meetsVersionReq = doesCurrentVersionMeetRequirement(VERSIONS_WITH_TALL_MENUBAR.TEST);
            break;
        case "prod":
            meetsVersionReq = doesCurrentVersionMeetRequirement(VERSIONS_WITH_TALL_MENUBAR.PROD);
            break;
    }
    return meetsVersionReq ? 600 : 440;
};

const DEFAULT_APP_SIZE: MenubarViewOption = "medium";
const APP_DIMENSIONS = {
    small: {
        height: 480,
        width: 630,
    },
    medium: {
        height: getHeightForAppVersion(),
        width: 680,
    },
    large: {
        height: 800,
        width: 1000,
    },
};

const getDimensionsForMenubarViewOptions = (view: MenubarViewOption, withContextMenu?: boolean) => {
    let height;
    let width = withContextMenu ? CONTEXT_MENU_DEFAULT_WIDTH_PX : 0;
    switch (view) {
        case "small": {
            height = APP_DIMENSIONS.small.height;
            width += APP_DIMENSIONS.small.width;
            break;
        }
        case "large": {
            height = APP_DIMENSIONS.large.height;
            width += APP_DIMENSIONS.large.width;
            break;
        }
        default: {
            height = APP_DIMENSIONS.medium.height;
            width += APP_DIMENSIONS.medium.width;
            break;
        }
    }
    return [height, width];
};

const OPEN_MAIN_WINDOW_BTN_LABEL = "Open LifeAt";

function MenuBarApp() {
    const { lightMode }: AppearanceContextType = useAppearanceContext();
    const { user, userData }: UserContextType = useUserContext();

    const appHeight = useRef(null);
    const appWidth = useRef(null);

    const [h, setH] = useState(getDimensionsForMenubarViewOptions(DEFAULT_APP_SIZE, false)[0]);
    const [w, setW] = useState(getDimensionsForMenubarViewOptions(DEFAULT_APP_SIZE, false)[1]);

    useProjectsData(user?.uid);

    const viewState = useMemo<MenubarViewOption>(() => DEFAULT_APP_SIZE, []);
    useEffect(() => {
        const [h, w] = getDimensionsForMenubarViewOptions(viewState);
        appHeight.current = h;
        appWidth.current = w;

        setH(h);
        setW(w);

        // Update the menubar window size
        if (window.todesktop) {
            window.todesktop.menubar.enlarge({ width: w, height: h });
        }
    }, [viewState]);

    const taskData = useTaskData({
        uid: user?.uid,
        filterService: MenubarFilterService(user?.uid),
    });

    let appContents = (
        <AppWrapper className={`menubar-app-wrapper`}>
            <AppHeader>{/* TODO: maybe */}</AppHeader>

            <AppBody className={`menubar-app-body`}>
                <AppComponents className={`menubar-app-components`}>
                    <BodyHalf className={`calendar-menubar-body`}>
                        <StyledCalendar
                            show={true}
                            amplitudeEventSource="menubar"
                            sendCalendarNotifications={!!window.todesktop}
                        />
                    </BodyHalf>

                    <BodyHalf className={`todo-menubar-body`}>
                        <StyledTaskList
                            {...widgetData.TaskListWidget}
                            taskData={taskData}
                            amplitudeEventSource="menubar"
                            isUserAuthenticated={!!user}
                            showTitle={false}
                            isOpen={true}
                            isStatic={true}
                            initialPos={{ x: 0, y: 0 }}
                            lightMode={lightMode}
                            savedLayout={{
                                wasOpen: true,
                                height: window.innerHeight,
                                width: window.innerWidth,
                                xPos: 0,
                                yPos: 0,
                            }}
                            footerAutoHide={false}
                            isResizable={false}
                        />
                    </BodyHalf>
                </AppComponents>
            </AppBody>

            <AppFooter className={`menubar-footer`}>
                <FooterUnit>
                    <FooterButton onClick={() => showMainWindow()}>{OPEN_MAIN_WINDOW_BTN_LABEL}</FooterButton>
                </FooterUnit>
            </AppFooter>
        </AppWrapper>
    );

    return (
        ToDesktopUtil.isWindowMenubar() &&
        userData && (
            <AppContainer className={`menubar-app-container`} style={{ height: h, width: w }}>
                {appContents}
            </AppContainer>
        )
    );
}
export default MenuBarApp;
